import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Share2, Heart, Users, Gift, User, Calendar, CreditCard as Edit, Download, DollarSign, TrendingUp, CheckCircle, Banknote, Clock, ArrowRightCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getRegistry } from '../lib/registry';
import { supabase } from '../lib/supabase';
import { exportRegistryToExcel } from '../utils/excelExport';
import { ThemeProvider } from '../components/ThemeProvider';
import ThemedLayout from '../components/ThemedLayout';
import ShareRegistryModal from '../components/ShareRegistryModal';
import GuestClaimModal from '../components/GuestClaimModal';
import GroupGiftModal from '../components/GroupGiftModal';
import SuccessMessage from '../components/SuccessMessage';
import ThankYouModal from '../components/ThankYouModal';
import EditRegistryModal from '../components/EditRegistryModal';
import EditEventsModal from '../components/EditEventsModal';
import EditItemModal from '../components/EditItemModal';
import ThemeCustomizer from '../components/ThemeCustomizer';
import InvitationAnimation from '../components/InvitationAnimation';
import PaymentModal from '../components/PaymentModal';
import RSVPTable from '../components/RSVPTable';
import OwnerInvitationSettings from '../components/invitations/OwnerInvitationSettings';
import TransactionBreakdown from '../components/TransactionBreakdown';
import WithdrawalRequestModal from '../components/WithdrawalRequestModal';
import { getRegistryWithdrawalRequests } from '../lib/admin';
import type { Registry, RegistryItem, RegistryEvent } from '../types/registry';
import type { ThemeConfig } from '../types/theme';
import type { RegistryWithdrawalRequest } from '../types/admin';

interface ClaimInfo {
  name: string;
  email: string;
  type: 'user' | 'guest' | 'payhere';
}

interface Contribution {
  contributor_name: string;
  amount: number;
  message?: string;
}

export default function RegistryDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [registry, setRegistry] = useState<Registry & { items: RegistryItem[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const [error, setError] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [showGroupGiftModal, setShowGroupGiftModal] = useState(false);
  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const [showEditRegistryModal, setShowEditRegistryModal] = useState(false);
  const [showEditEventsModal, setShowEditEventsModal] = useState(false);
  const [showEditItemModal, setShowEditItemModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<RegistryItem | null>(null);
  const [events, setEvents] = useState<RegistryEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<string>('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessageType, setSuccessMessageType] = useState<'claim' | 'contribution'>('claim');
  const [claimInfo, setClaimInfo] = useState<Record<string, ClaimInfo>>({});
  const [contributions, setContributions] = useState<Record<string, Contribution[]>>({});
  const [theme, setTheme] = useState<ThemeConfig>({
    backgroundImage: '',
    textColor: '#ffffff',
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    opacity: 0.75,
    fontFamily: 'IntroRust',
    buttonColor: '#a855f7',
    buttonTextColor: '#ffffff',
    introAnimation: 'none',
    introTitle: '',
    introSubtitle: '',
  });
  const [showIntroAnimation, setShowIntroAnimation] = useState(false);
  const [showFeeInfo, setShowFeeInfo] = useState(false);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [withdrawalRequests, setWithdrawalRequests] = useState<RegistryWithdrawalRequest[]>([]);
  const [availableBalance, setAvailableBalance] = useState(0);

  const isOwner = user?.id === registry?.user_id;

  useEffect(() => {
    console.log('Registry ID from params:', id);
    if (id) {
      loadRegistry();
    } else {
      console.error('No registry ID provided');
      setError('No registry ID provided');
      setLoading(false);
    }
    const cleanup = subscribeToChanges();
    return () => cleanup();
  }, [id]);

  const loadAvailableBalance = useCallback(async () => {
    if (!id || !isOwner) return;
    try {
      const { data, error } = await supabase.rpc('owner_available_balance', { p_registry_id: id });
      if (!error && data !== null) setAvailableBalance(Number(data));
    } catch (err) {
      console.error('Error loading available balance:', err);
    }
  }, [id, isOwner]);

  useEffect(() => {
    if (registry) {
      loadEvents();
      if (isOwner) {
        loadClaimInfo();
        loadContributions();
        loadWithdrawalRequests();
        loadAvailableBalance();
      }
      if (registry.theme) {
        setTheme(registry.theme);
      }
      const anim = registry.theme?.introAnimation || 'none';
      if (anim !== 'none' && !isOwner) {
        const sessionKey = `inv-anim-${registry.id}`;
        if (!sessionStorage.getItem(sessionKey)) {
          setShowIntroAnimation(true);
        }
      }
    }
  }, [registry, isOwner]);

  const handleIntroAnimationComplete = () => {
    if (registry) {
      sessionStorage.setItem(`inv-anim-${registry.id}`, 'played');
    }
    setShowIntroAnimation(false);
  };


  const loadClaimInfo = async () => {
    if (!registry) return;

    const claimedItems = registry.items.filter(item => item.is_claimed);
    const info: Record<string, ClaimInfo> = {};

    for (const item of claimedItems) {
      if (item.claimed_by) {
        const { data: userData } = await supabase
          .from('profiles')
          .select('username, email')
          .eq('id', item.claimed_by)
          .single();

        if (userData) {
          info[item.id] = {
            name: userData.username,
            email: userData.email || '',
            type: 'user'
          };
        }
      } else if (item.guest_claimed_by) {
        const { data: guestData } = await supabase
          .from('guest_users')
          .select('name, email')
          .eq('id', item.guest_claimed_by)
          .single();

        if (guestData) {
          info[item.id] = {
            name: guestData.name,
            email: guestData.email,
            type: 'guest'
          };
        }
      } else {
        const { data: claimData } = await supabase
          .from('registry_claims')
          .select('claimer_name')
          .eq('item_id', item.id)
          .order('claimed_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (claimData) {
          info[item.id] = {
            name: claimData.claimer_name,
            email: '',
            type: 'payhere'
          };
        }
      }
    }

    setClaimInfo(info);
  };

  const loadContributions = async () => {
    if (!registry) return;

    const contribItems = registry.items.filter(item => item.allows_group_gifting || item.is_money_gift);
    const contribs: Record<string, Contribution[]> = {};

    for (const item of contribItems) {
      const { data } = await supabase
        .from('group_gift_contributions')
        .select('contributor_name, amount, message')
        .eq('item_id', item.id)
        .order('created_at', { ascending: false });

      if (data) {
        contribs[item.id] = data;
      }
    }

    setContributions(contribs);
  };

  const loadWithdrawalRequests = async () => {
    if (!registry) return;
    try {
      const data = await getRegistryWithdrawalRequests(registry.id);
      setWithdrawalRequests(data);
    } catch (err) {
      console.error('Error loading withdrawal requests:', err);
    }
  };

  const loadEvents = async () => {
    try {
      const { data, error } = await supabase
        .from('registry_events')
        .select('*')
        .eq('registry_id', id)
        .order('date', { ascending: true });

      if (error) throw error;
      if (data) {
        setEvents(data);
        if (data.length > 0 && user) {
          setSelectedEvent(data[0].id);
        }
      }
    } catch (err) {
      console.error('Error loading events:', err);
    }
  };

  const loadRegistry = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('Loading registry with ID:', id);
      
      if (!id) {
        throw new Error('Registry ID is required');
      }

      const data = await getRegistry(id);
      console.log('Registry data loaded:', data);
      setRegistry(data);
    } catch (err) {
      console.error('Error loading registry:', err);
      setError(err instanceof Error ? err.message : 'Failed to load registry');
    } finally {
      setLoading(false);
    }
  };

  const subscribeToChanges = () => {
    if (!id) return () => {};

    const channel = supabase
      .channel(`registry-${id}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'registry_items',
        filter: `registry_id=eq.${id}`
      }, () => {
        loadRegistry();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const handleClaim = (item: RegistryItem) => {
    setSelectedItem(item);
    if (user) {
      setShowPaymentModal(true);
    } else {
      setShowClaimModal(true);
    }
  };

  const handleGroupGift = (item: RegistryItem) => {
    const safeTarget = item.target_amount && Number(item.target_amount) > 0 ? Number(item.target_amount) : 0;
    const collectedAmt = Number(item.collected_amount || 0);
    if (safeTarget > 0 && collectedAmt >= safeTarget) return;
    setSelectedItem(item);
    setShowGroupGiftModal(true);
  };

  const handleThankYou = (item: RegistryItem) => {
    setSelectedItem(item);
    setShowThankYouModal(true);
  };

  const handleClaimSuccess = () => {
    setShowClaimModal(false);
    setShowPaymentModal(false);
    setSuccessMessageType('claim');
    setShowSuccessMessage(true);
  };

  const handleContributionSuccess = () => {
    setShowGroupGiftModal(false);
    setSuccessMessageType('contribution');
    setShowSuccessMessage(true);
    loadRegistry();
  };

  const handleSuccessClose = () => {
    setShowSuccessMessage(false);
    loadRegistry();
  };

  const handleEditRegistryClose = useCallback(() => {
    setShowEditRegistryModal(false);
  }, []);

  const handleExport = async () => {
    if (!registry) return;
    await exportRegistryToExcel(registry);
  };

  const handleSkipGift = () => {
    navigate(-1);
  };

  const getFeeResponsibilityLabel = (responsibility: string) => {
    switch (responsibility) {
      case 'GUEST': return 'Guest Pays Fee';
      case 'COUPLE': return 'Couple Absorbs Fee';
      case 'SPLIT': return 'Fee Split 50/50';
      default: return 'Guest Pays Fee';
    }
  };

  const buttonStyle: React.CSSProperties = {
    backgroundColor: theme.buttonColor || '#a855f7',
    color: theme.buttonTextColor || '#ffffff',
  };
  const progressColor = theme.buttonColor || '#a855f7';

  if (loading) {
    return (
      <div className="min-h-screen bg-pastel-gradient flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pastel-purple-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-pastel-gradient flex items-center justify-center">
        <div className="text-center py-12">
          <p className="text-red-500 text-lg mb-4">{error}</p>
          <button
            onClick={() => navigate('/registries')}
            className="btn-pastel"
          >
            Back to Registries
          </button>
        </div>
      </div>
    );
  }

  if (!registry) {
    return (
      <div className="min-h-screen bg-pastel-gradient flex items-center justify-center">
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg mb-4">Registry not found</p>
          <button
            onClick={() => navigate('/registries')}
            className="btn-pastel"
          >
            Back to Registries
          </button>
        </div>
      </div>
    );
  }

  // Hide closed registries from public view (non-owners see a closed message)
  const isClosed = registry.is_closed === true;
  if (isClosed && !isOwner) {
    return (
      <div className="min-h-screen bg-pastel-gradient flex items-center justify-center">
        <div className="text-center py-12 max-w-md">
          <CheckCircle className="w-12 h-12 text-pastel-green-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">This registry is no longer accepting gifts</h2>
          <p className="text-gray-500 mb-6">The registry has been closed. Thank you to everyone who contributed!</p>
          <button
            onClick={() => navigate('/registries')}
            className="btn-pastel"
          >
            Back to Registries
          </button>
        </div>
      </div>
    );
  }

  // Owner collection summary values
  const groupGiftItems = registry.items.filter(i => i.allows_group_gifting);
  const moneyGiftItems = registry.items.filter(i => i.is_money_gift);
  const claimedCount = registry.items.filter(i => i.is_claimed && !i.is_money_gift).length;
  const totalGroupGiftCollected = groupGiftItems.reduce((sum, i) => sum + Number(i.collected_amount || 0), 0);
  const totalGroupGiftTarget = groupGiftItems.reduce((sum, i) => sum + Number(i.target_amount || 0), 0);
  const totalMoneyGiftCollected = moneyGiftItems.reduce((sum, i) => sum + Number(i.collected_amount || 0), 0);
  const regularClaimedValue = registry.items
    .filter(i => i.is_claimed && !i.allows_group_gifting && !i.is_money_gift)
    .reduce((sum, i) => sum + Number(i.price || 0), 0);
  const totalCollectedValue = totalGroupGiftCollected + regularClaimedValue + totalMoneyGiftCollected;

  return (
    <ThemeProvider initialTheme={theme}>
      <ThemedLayout>
        {showIntroAnimation && registry && (
          <InvitationAnimation
            animationType={theme.introAnimation || 'none'}
            title={theme.introTitle || registry.title}
            subtitle={theme.introSubtitle}
            theme={theme}
            onComplete={handleIntroAnimationComplete}
          />
        )}
        <div className="max-w-4xl mx-auto space-y-8 px-4 sm:px-6 py-8">
          {isOwner && location.state?.creationWarning && <p role="alert" className="mt-6 bg-amber-50 text-amber-900 border border-amber-200 rounded p-4">{location.state.creationWarning}</p>}
          {/* Header */}
          <div className="flex flex-col gap-4">
            <div className="min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-center sm:gap-4">
                <h1 className="text-2xl sm:text-3xl font-bold break-words">{registry.title}</h1>
                {!user && (
                  <div className="flex items-center mt-2 sm:mt-0 text-base">
                    <Calendar className="w-4 h-4 mr-2 flex-shrink-0" />
                    <span className="break-words">{new Date(registry.event_date).toLocaleDateString(undefined, {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}</span>
                  </div>
                )}
              </div>
              {registry.description && (
                <p className="mt-2 text-sm sm:text-base break-words">{registry.description}</p>
              )}
              {events.length > 0 && user && (
                <div className="mt-4">
                  <label className="block text-sm font-medium mb-1">Select Event</label>
                  <select
                    value={selectedEvent}
                    onChange={(e) => setSelectedEvent(e.target.value)}
                    className="w-full sm:w-auto px-3 py-2 bg-white/60 border border-white/40 rounded-md focus:ring-pastel-purple-400 focus:border-pastel-purple-400 text-sm"
                  >
                    {events.map(event => (
                      <option key={event.id} value={event.id}>
                        {event.name} - {new Date(event.date).toLocaleDateString()}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="mt-2 space-y-1 text-sm">
                {user && <p>Event Date: {new Date(registry.event_date).toLocaleDateString()}</p>}
                {registry.delivery_address && (
                  <p className="break-words">Delivery Address: {registry.delivery_address}</p>
                )}
                {registry.contact_number && (
                  <p>Contact: {registry.contact_number}</p>
                )}
              </div>
            </div>

            {/* Action buttons — scrollable row on mobile */}
            <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1 sm:mx-0 sm:px-0 sm:flex-wrap sm:overflow-visible">
              {isOwner && (
                <>
                  <button
                    onClick={() => setShowEditRegistryModal(true)}
                    className="flex-shrink-0 flex items-center space-x-1.5 px-3 py-2 bg-white/60 rounded-lg hover:bg-white/80 text-sm whitespace-nowrap transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                    <span>Edit Registry</span>
                  </button>
                  <button
                    onClick={() => setShowEditEventsModal(true)}
                    className="flex-shrink-0 flex items-center space-x-1.5 px-3 py-2 bg-white/60 rounded-lg hover:bg-white/80 text-sm whitespace-nowrap transition-colors"
                  >
                    <Calendar className="w-4 h-4" />
                    <span>Manage Events</span>
                  </button>
                  <button
                    onClick={handleExport}
                    className="flex-shrink-0 flex items-center space-x-1.5 px-3 py-2 bg-white/60 rounded-lg hover:bg-white/80 text-sm whitespace-nowrap transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    <span>Export</span>
                  </button>
                </>
              )}
              <button
                onClick={() => setShowShareModal(true)}
                className="flex-shrink-0 flex items-center space-x-1.5 px-3 py-2 bg-white/60 rounded-lg hover:bg-white/80 text-sm whitespace-nowrap transition-colors"
              >
                <Share2 className="w-4 h-4" />
                <span>Share</span>
              </button>
            </div>
          </div>

          {/* Owner Collection Summary */}
          {isOwner && (
            <div className="card-pastel p-4 sm:p-5 space-y-4">
              <h2 className="text-sm font-semibold flex items-center gap-2 uppercase tracking-wide">
                <TrendingUp className="w-4 h-4 text-pastel-green-600" />
                Collection Summary
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-white/60 rounded-lg p-3 text-center">
                  <p className="text-xs mb-1">Total Collected</p>
                  <p className="text-base font-bold text-pastel-green-600">{registry.currency} {totalCollectedValue.toFixed(2)}</p>
                </div>
                <div className="bg-white/60 rounded-lg p-3 text-center">
                  <p className="text-xs mb-1">Group Gifts</p>
                  <p className="text-base font-bold text-pastel-blue-600">{registry.currency} {totalGroupGiftCollected.toFixed(2)}</p>
                  {totalGroupGiftTarget > 0 && (
                    <p className="text-xs mt-0.5">of {registry.currency} {totalGroupGiftTarget.toFixed(2)}</p>
                  )}
                </div>
                <div className="bg-white/60 rounded-lg p-3 text-center">
                  <p className="text-xs mb-1">Gifts Claimed</p>
                  <p className="text-base font-bold">{claimedCount} / {registry.items.length}</p>
                </div>
                <div className="bg-white/60 rounded-lg p-3 text-center">
                  <p className="text-xs mb-1">Regular Gifts Value</p>
                  <p className="text-base font-bold text-pastel-pink-600">{registry.currency} {regularClaimedValue.toFixed(2)}</p>
                </div>
              </div>
              {groupGiftItems.length > 0 && (
                <div className="space-y-3">
                  <p className="text-xs font-medium uppercase tracking-wide">Group Gift Progress</p>
                  {groupGiftItems.map(i => {
                    const st = i.target_amount && Number(i.target_amount) > 0 ? Number(i.target_amount) : 0;
                    const ca = Number(i.collected_amount || 0);
                    const pct = st > 0 ? Math.min((ca / st) * 100, 100) : 0;
                    return (
                      <div key={i.id} className="space-y-1.5">
                        <div className="flex justify-between text-sm gap-2">
                          <span className="truncate">{i.name}</span>
                          <span className="flex-shrink-0 text-pastel-green-600 font-medium">
                            {registry.currency} {ca.toFixed(2)} / {registry.currency} {st.toFixed(2)}
                          </span>
                        </div>
                        <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-pastel-green-500 to-pastel-green-400 transition-all duration-300"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <p className="text-xs">{pct.toFixed(1)}% funded</p>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Withdrawal section */}
              {!isClosed && availableBalance > 0 && (
                <div className="pt-3 border-t border-white/40">
                  <button
                    onClick={() => setShowWithdrawalModal(true)}
                    className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-pastel-green-500 text-white rounded-lg hover:bg-pastel-green-600 transition-colors text-sm font-medium"
                  >
                    <Banknote className="w-4 h-4" />
                    Request Withdrawal
                  </button>
                </div>
              )}

              {/* Withdrawal history */}
              {withdrawalRequests.length > 0 && (
                <div className="pt-3 border-t border-white/40 space-y-2">
                  <p className="text-xs font-medium uppercase tracking-wide">Withdrawal History</p>
                  {withdrawalRequests.map((wr) => (
                    <div key={wr.id} className="flex items-center justify-between bg-white/60 rounded-lg p-2.5 text-sm">
                      <div className="flex items-center gap-2">
                        {wr.status === 'pending' && <Clock className="w-4 h-4 text-yellow-500" />}
                        {wr.status === 'approved' && <ArrowRightCircle className="w-4 h-4 text-pastel-blue-600" />}
                        {wr.status === 'transferred' && <CheckCircle className="w-4 h-4 text-pastel-green-600" />}
                        <span className="text-gray-800 font-medium">{registry.currency} {Number(wr.amount).toFixed(2)}</span>
                      </div>
                      <div className="text-right">
                        <span className={`text-xs font-medium ${
                          wr.status === 'pending' ? 'text-yellow-500' :
                          wr.status === 'approved' ? 'text-pastel-blue-600' :
                          wr.status === 'transferred' ? 'text-pastel-green-600' : 'text-red-500'
                        }`}>
                          {wr.status.charAt(0).toUpperCase() + wr.status.slice(1)}
                        </span>
                        <p className="text-xs">{new Date(wr.requested_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {isClosed && (
                <div className="pt-3 border-t border-white/40">
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-pastel-green-600" />
                    <span>This registry has been closed and all funds have been transferred.</span>
                  </div>
                </div>
              )}
            </div>
          )}

          <div id="gift-section" className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {registry.items.map((item) => {
              const safeTarget = item.target_amount && Number(item.target_amount) > 0 ? Number(item.target_amount) : 0;
              const collectedAmt = Number(item.collected_amount || 0);
              const progressPercentage = item.allows_group_gifting && safeTarget > 0
                ? Math.min((collectedAmt / safeTarget) * 100, 100)
                : 0;

              return (
                <div
                  key={item.id}
                  className="card-pastel overflow-hidden hover:bg-white/80 transition-colors"
                >
                  <div className="p-4 sm:p-5 space-y-4">
                    <div className="flex justify-between items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <h3 className="font-medium break-words leading-snug">{item.name}</h3>
                        {(isOwner || !item.allows_group_gifting) && (
                          <p className="text-sm mt-0.5">
                            {registry.currency} {item.price}
                          </p>
                        )}
                      </div>
                    </div>

                    {item.url && (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block hover:text-pastel-purple-600 text-sm underline"
                      >
                        View Product
                      </a>
                    )}

                    {item.is_claimed && !item.allows_group_gifting && !item.is_money_gift ? (
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2 text-sm">
                          <User className="w-4 h-4 flex-shrink-0" />
                          <span className="break-words">
                            {isOwner ? (
                              <>
                                Claimed by {claimInfo[item.id]?.name || 'Anonymous'}
                                {claimInfo[item.id]?.email && (
                                  <span className="block text-xs mt-1">
                                    {claimInfo[item.id].email}
                                  </span>
                                )}
                                {item.claimed_by && (
                                  <button
                                    onClick={() => handleThankYou(item)}
                                    className="block mt-2 text-pastel-purple-600 hover:text-pastel-purple-700"
                                  >
                                    Send Thank You
                                  </button>
                                )}
                              </>
                            ) : (
                              'This gift has been claimed'
                            )}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col space-y-3">
                        {item.is_money_gift ? (
                          <>
                            <div className="w-full space-y-2">
                              <div className="flex items-center gap-2 text-sm">
                                <Banknote className="w-4 h-4 text-pastel-green-600" />
                                <span className="text-pastel-green-600 font-medium">Money Gift</span>
                                <span className="text-gray-500">· Multiple guests can contribute</span>
                              </div>
                              {collectedAmt > 0 && (
                                <p className="text-sm">
                                  {registry.currency} {collectedAmt.toFixed(2)} collected
                                </p>
                              )}
                              {isOwner && contributions[item.id]?.length > 0 && (
                                <div className="mt-4 space-y-2">
                                  <h4 className="text-sm font-medium flex items-center">
                                    <DollarSign className="w-4 h-4 mr-1" />
                                    Contributors
                                  </h4>
                                  <div className="space-y-2">
                                    {contributions[item.id].map((contribution, index) => (
                                      <div key={index} className="text-sm flex justify-between gap-2">
                                        <span className="min-w-0 truncate">{contribution.contributor_name}</span>
                                        <span className="flex-shrink-0">{registry.currency} {Number(contribution.amount).toFixed(2)}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              <button
                                onClick={() => handleClaim(item)}
                                style={buttonStyle}
                                className="w-full flex items-center justify-center space-x-2 px-4 py-2 rounded-md hover:opacity-90 transition-opacity text-sm font-medium"
                              >
                                <Banknote className="w-4 h-4" />
                                <span>Send Money Gift</span>
                              </button>
                            </div>
                          </>
                        ) : item.allows_group_gifting ? (
                          <>
                            <div className="w-full space-y-2">
                              <div className="flex justify-between text-sm">
                                <span>Progress: {progressPercentage.toFixed(1)}%</span>
                                {isOwner && (
                                  <span>
                                    {registry.currency} {collectedAmt.toFixed(2)} of {registry.currency} {safeTarget.toFixed(2)}
                                  </span>
                                )}
                              </div>
                              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className="h-full transition-all duration-300"
                                  style={{ width: `${progressPercentage}%`, backgroundColor: progressColor }}
                                />
                              </div>
                              {isOwner && contributions[item.id]?.length > 0 && (
                                <div className="mt-4 space-y-2">
                                  <h4 className="text-sm font-medium flex items-center">
                                    <DollarSign className="w-4 h-4 mr-1" />
                                    Contributors
                                  </h4>
                                  <div className="space-y-2">
                                    {contributions[item.id].map((contribution, index) => (
                                      <div key={index} className="text-sm flex justify-between gap-2">
                                        <span className="min-w-0 truncate">{contribution.contributor_name}</span>
                                        <span className="flex-shrink-0">{registry.currency} {Number(contribution.amount).toFixed(2)}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              <button
                                onClick={() => handleGroupGift(item)}
                                disabled={item.is_claimed && progressPercentage >= 100}
                                style={item.is_claimed && progressPercentage >= 100 ? undefined : buttonStyle}
                                className={`w-full flex items-center justify-center space-x-2 px-4 py-2 ${
                                  item.is_claimed && progressPercentage >= 100 
                                    ? 'bg-pastel-green-500 text-white' 
                                    : 'hover:opacity-90 transition-opacity'
                                } rounded-md text-sm font-medium ${
                                  item.is_claimed && progressPercentage >= 100 ? 'cursor-default' : ''
                                }`}
                              >
                                <Gift className="w-4 h-4" />
                                <span>
                                  {item.is_claimed && progressPercentage >= 100 
                                    ? 'Fully Funded!' 
                                    : 'Contribute'}
                                </span>
                              </button>
                            </div>
                          </>
                        ) : (
                          <button
                            onClick={() => handleClaim(item)}
                            style={buttonStyle}
                            className="flex items-center justify-center space-x-2 px-8 py-4 rounded-2xl shadow-soft-lg hover:opacity-90 transition-opacity text-sm font-semibold backdrop-blur-sm"
                          >
                            <Gift className="w-4 h-4" />
                            <span>Claim Gift</span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {!user && (
            <div className="flex justify-center mt-8">
              <button
                onClick={handleSkipGift}
                className="px-6 py-2 bg-white/60 rounded-md hover:bg-white/80 transition-colors"
                aria-label="Skip gift selection for now"
              >
                Skip Gift For Now
              </button>
            </div>
          )}

          {isOwner && registry && <OwnerInvitationSettings key={registry.id} registry={registry} events={events} giftRegistryAvailable={registry.items.length > 0 && !registry.is_closed} onSaved={nextTheme => { setTheme(nextTheme); setRegistry(current => current ? { ...current, theme: nextTheme } : current); }} />}

          {isOwner && (
            <div className="mt-8 card-pastel p-4 sm:p-6">
              <RSVPTable registryId={id!} />
            </div>
          )}
        </div>
        
        {isOwner && (
          <ThemeCustomizer
            registryId={id!}
            initialTheme={registry?.theme as ThemeConfig}
            onThemeChange={setTheme}
          />
        )}

        {showShareModal && (
          <ShareRegistryModal
            registryId={id!}
            onClose={() => setShowShareModal(false)}
          />
        )}

        {showClaimModal && selectedItem && (
          <GuestClaimModal
            item={selectedItem}
            onClose={() => setShowClaimModal(false)}
            onClaim={handleClaimSuccess}
          />
        )}

        {showPaymentModal && selectedItem && registry && (
          <PaymentModal
            item={selectedItem}
            currency={registry.currency}
            onClose={() => setShowPaymentModal(false)}
            onSuccess={handleClaimSuccess}
            feeResponsibility={registry.fee_responsibility}
            platformFeePercentage={registry.platform_fee_percentage}
            gatewayFeePercentage={registry.gateway_fee_percentage}
          />
        )}

        {showGroupGiftModal && selectedItem && registry && (
          <GroupGiftModal
            item={selectedItem}
            registry={registry}
            onClose={() => setShowGroupGiftModal(false)}
            onContribute={handleContributionSuccess}
          />
        )}

        {showSuccessMessage && selectedItem && (
          <SuccessMessage
            type={successMessageType}
            item={selectedItem}
            onClose={handleSuccessClose}
          />
        )}

        {showThankYouModal && selectedItem && (
          <ThankYouModal
            item={selectedItem}
            registryId={registry.id}
            onClose={() => {
              setShowThankYouModal(false);
              setSelectedItem(null);
            }}
          />
        )}

        {showEditRegistryModal && (
          <EditRegistryModal
            registry={registry}
            onClose={handleEditRegistryClose}
            onUpdate={loadRegistry}
          />
        )}

        {showEditEventsModal && (
          <EditEventsModal
            registryId={registry.id}
            events={events}
            onClose={() => setShowEditEventsModal(false)}
            onUpdate={loadEvents}
          />
        )}

        {showEditItemModal && selectedItem && (
          <EditItemModal
            item={selectedItem}
            currency={registry.currency}
            onClose={() => {
              setShowEditItemModal(false);
              setSelectedItem(null);
            }}
            onUpdate={loadRegistry}
          />
        )}

        {showWithdrawalModal && registry && (
          <WithdrawalRequestModal
            registry={registry}
            maxAmount={availableBalance}
            onClose={() => setShowWithdrawalModal(false)}
            onSuccess={() => {
              setShowWithdrawalModal(false);
              loadWithdrawalRequests();
            }}
          />
        )}
      </ThemedLayout>
    </ThemeProvider>
  );
}